import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CheckCircle2, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { fiscalidadAPI } from '../api';

// Avisos de plazo de los modelos 303, 130, 111, 390 y 190.
// Avisa a 10, 5 y 2 días del cierre, el mismo día y cuando ya ha pasado.
const ESTILOS = {
  fuera_de_plazo: { fondo: 'bg-red-50 border-red-300 text-red-900', Icono: AlertTriangle },
  vence_hoy: { fondo: 'bg-red-50 border-red-300 text-red-900', Icono: AlertTriangle },
  critico: { fondo: 'bg-orange-50 border-orange-300 text-orange-900', Icono: Clock },
  urgente: { fondo: 'bg-amber-50 border-amber-200 text-amber-900', Icono: Clock },
  proximo: { fondo: 'bg-slate-50 border-slate-200 text-slate-700', Icono: CalendarClock },
  presentado: { fondo: 'bg-green-50 border-green-200 text-green-800', Icono: CheckCircle2 },
  lejano: { fondo: 'bg-slate-50 border-slate-200 text-slate-600', Icono: CalendarClock },
};

const eur = (n) => `${Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const AvisosFiscales = ({ ejercicio, entidadId, compacto = false, titulo = 'Plazos de presentación' }) => {
  const [datos, setDatos] = useState(null);

  useEffect(() => {
    let vivo = true;
    fiscalidadAPI.getPlazos(ejercicio, entidadId)
      .then(r => { if (vivo) setDatos(r.data); })
      .catch(() => { if (vivo) setDatos(null); });
    return () => { vivo = false; };
  }, [ejercicio, entidadId]);

  const avisos = (datos?.avisos || []).filter(a => !compacto || a.estado !== 'presentado');
  if (!datos || avisos.length === 0) return null;

  const cuerpo = (
    <div className="space-y-2" data-testid="avisos-fiscales-lista">
      {avisos.map((a) => {
        const { fondo, Icono } = ESTILOS[a.estado] || ESTILOS.proximo;
        return (
          <div key={`${a.modelo}-${a.trimestre || 'A'}`}
               className={`flex items-start gap-2 rounded border px-3 py-2 text-xs ${fondo}`}
               data-testid={`aviso-${a.modelo}-${a.trimestre || 'anual'}`}>
            <Icono className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">
                Modelo {a.modelo} · {a.periodo} · {a.nombre}
              </p>
              <p>{a.mensaje}</p>
              {a.presentado ? (
                <p className="text-[11px] opacity-80">
                  Presentado{a.fecha_presentacion ? ` el ${a.fecha_presentacion.split('-').reverse().join('/')}` : ''}
                  {a.importe_presentado != null ? ` · ${eur(a.importe_presentado)}` : ''}
                </p>
              ) : (
                <>
                  <p className="text-[11px] opacity-80">
                    {a.importe_calculado != null && <>A ingresar según el cálculo de hoy: <b>{eur(a.importe_calculado)}</b> · </>}
                    Hasta el {a.fecha_limite.split('-').reverse().join('/')}
                  </p>
                  {a.mensaje_domiciliacion && (
                    <p className={`text-[11px] ${a.estado_domiciliacion === 'cerrada' ? 'opacity-70' : 'font-medium'}`}
                       data-testid={`aviso-domiciliacion-${a.modelo}-${a.trimestre || 'anual'}`}>
                      {a.mensaje_domiciliacion}
                    </p>
                  )}
                </>
              )}
              {a.acompana && (
                <p className="text-[11px] opacity-80 italic"
                   data-testid={`aviso-acompana-${a.modelo}-${a.trimestre || 'anual'}`}>
                  {a.acompana.texto}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (compacto) {
    return (
      <Card data-testid="avisos-fiscales">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <CalendarClock className="w-4 h-4" /> {titulo}
            <Link to="/contabilidad" className="ml-auto text-xs font-normal text-indigo-600 hover:underline">
              Ir a Fiscalidad
            </Link>
          </CardTitle>
        </CardHeader>
        <CardContent>{cuerpo}</CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="avisos-fiscales">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CalendarClock className="w-4 h-4" /> {titulo} · {ejercicio}
          {datos.pendientes > 0 && (
            <span className="ml-2 text-xs font-normal text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5">
              {datos.pendientes} pendiente(s)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {cuerpo}
        <p className="text-[11px] text-slate-500 pt-2">
          Los plazos ya tienen en cuenta fines de semana y festivos nacionales, y el cierre de la
          domiciliación bancaria (5 días antes: el día 15 en los trimestres normales). En enero, el
          303 del 4T va con el 390 y el 111 del 4T con el 190; el 130 del 4T vence el día 20. Un
          trimestre sin retenciones no genera aviso del modelo 111.
        </p>
      </CardContent>
    </Card>
  );
};

export default AvisosFiscales;
